/**
 * PATH: apps/ai/src/services/agents/skill-router.ts
 * PURPOSE: Matches user queries to specialized skills (email, win prediction, reports, etc.)
 */

import type { ExtractedEntities, SkillMatch } from './agent-types';

export class SkillRouterAgent {
  private static SKILL_PATTERNS: Record<string, {
    patterns: RegExp[];
    requiredParams: string[];
    paramExtractors: Record<string, (query: string, entities: ExtractedEntities) => unknown>;
  }> = {
    'email-composer': {
      patterns: [/\b(compose|write|draft|create)\s+(an?\s+)?email\b/i, /\bemail\s+(for|about|regarding|to)\s+/i, /\bsend\s+(an?\s+)?email\b/i, /\bquote\s+email\b/i],
      requiredParams: ['quoteId'],
      paramExtractors: {
        quoteId: (q, e) => { const m = q.match(/quote\s*#?\s*(\d+)/i); return m ? parseInt(m[1]) : e.searchTerms?.[0]; },
        emailType: (q) => { if (/follow[\s-]?up|reminder/i.test(q)) return 'followup'; if (/revision|updated?/i.test(q)) return 'revision'; if (/expir|deadline/i.test(q)) return 'reminder'; return 'initial'; },
      },
    },
    'win-prediction': {
      patterns: [/\b(what('s|\s+is)\s+(the\s+)?)?win\s+(probability|chance|likelihood)\b/i, /\b(will\s+(we|I)|can\s+(we|I))\s+win\s+(this\s+)?quote\b/i, /\bpredict\s+(quote|outcome|win)\b/i, /\bchance\s+of\s+winning\b/i],
      requiredParams: ['quoteId'],
      paramExtractors: { quoteId: (q) => { const m = q.match(/quote\s*#?\s*(\d+)/i); return m ? parseInt(m[1]) : null; } },
    },
    'followup-suggestions': {
      patterns: [/\b(what|which)\s+(quotes?|leads?)\s+(need|require|should)\s+(follow[\s-]?up|attention)\b/i, /\bfollow[\s-]?ups?\b.*\btoday\b/i, /\b(pending|open)\s+(quotes?|proposals?)\b/i, /\bquotes?\s+to\s+follow[\s-]?up\b/i],
      requiredParams: [],
      paramExtractors: {},
    },
    'job-analysis': {
      patterns: [/\b(deep\s+)?analy[sz]e\s+(job|project)\s*#?\s*\d+\b/i, /\bjob\s*#?\s*(\d+)\s+(deep\s+)?analysis\b/i, /\b(detailed?|comprehensive)\s+(job|project)\s+review\b/i, /\bmoney\s+moves?\s+(for|on)\s+job\b/i],
      requiredParams: ['jobId'],
      paramExtractors: { jobId: (q, e) => { const m = q.match(/job\s*#?\s*(\d+)/i); return m ? parseInt(m[1]) : e.jobIds?.[0]; } },
    },
    'outlook-draft': {
      patterns: [/\bcreate\s+(outlook\s+)?draft\b/i, /\boutlook\s+(email\s+)?draft\b/i, /\bopen\s+in\s+outlook\b/i],
      requiredParams: [],
      paramExtractors: { subject: (q) => { const m = q.match(/(?:subject|about)\s*[:\-]?\s*["']?([^"'\n]+)["']?/i); return m ? m[1].trim() : null; } },
    },
    'generate-report': {
      patterns: [/\b(generate|create|build|make)\s+(a\s+)?(portfolio|job|financial|performance)\s+report\b/i, /\breport\s+(on|for|about)\s+/i, /\bpdf\s+report\b/i, /\bexport\s+(to\s+)?pdf\b/i],
      requiredParams: [],
      paramExtractors: {
        reportType: (q) => { if (/portfolio/i.test(q)) return 'portfolio-health'; if (/job\s+performance/i.test(q)) return 'job-performance'; if (/financial|cash|ar/i.test(q)) return 'financial-summary'; return 'portfolio-health'; },
        jobId: (q) => { const m = q.match(/job\s*#?\s*(\d+)/i); return m ? parseInt(m[1]) : null; },
      },
    },
  };

  match(query: string, entities: ExtractedEntities): SkillMatch[] {
    const matches: SkillMatch[] = [];

    for (const [skillId, config] of Object.entries(SkillRouterAgent.SKILL_PATTERNS)) {
      const patternMatches = config.patterns.filter(p => p.test(query));
      if (patternMatches.length === 0) continue;

      const confidence = Math.min(1.0, patternMatches.length * 0.4);
      const params: Record<string, unknown> = {};
      const missingParams: string[] = [];

      for (const [paramName, extractor] of Object.entries(config.paramExtractors)) {
        const value = extractor(query, entities);
        if (value !== null && value !== undefined) params[paramName] = value;
      }

      for (const required of config.requiredParams) {
        if (!params[required]) missingParams.push(required);
      }

      matches.push({ skillId, confidence, params, canExecute: missingParams.length === 0, missingParams: missingParams.length > 0 ? missingParams : undefined });
    }

    return matches.sort((a, b) => b.confidence - a.confidence);
  }

  getMissingParamsPrompt(skillId: string, missingParams: string[]): string {
    const prompts: Record<string, Record<string, string>> = {
      'email-composer': { quoteId: 'Which quote would you like to compose an email for? Please provide the quote number.' },
      'win-prediction': { quoteId: 'Which quote would you like me to predict the win probability for? Please provide the quote number.' },
      'job-analysis': { jobId: 'Which job would you like me to analyze? Please provide the job number.' },
    };
    const skillPrompts = prompts[skillId] || {};
    return missingParams.map(p => skillPrompts[p] || `Please provide: ${p}`).join('\n');
  }
}
